import React, { PropsWithChildren } from 'react'
import { classNames } from '../wrapper'

interface GenericSelectProps<TOption> {
    /** The data to display as <option> elements */
    options: TOption[];

    /** How to get the displayed text for each option */
    textFunc: (item: TOption) => string;

    /** How to get the unique value for each option (string or number) */
    valueFunc: (item: TOption) => string | number;

    /**
     * Current selection.
     * - If `multiple = false`, this can be a single string/number or null.
     * - If `multiple = true`, this should be an array of strings/numbers or null.
     */
    value: string | number | string[] | number[] | null;

    /**
     * Called when the user changes selection.
     * - If `multiple = false`, gets called with a single string/number or null.
     * - If `multiple = true`, gets called with an array of selected values (empty when nothing is selected).
     */
    onChange: (
        newValue: string | number | (string | number)[] | TOption | TOption[] | null
    ) => void;

    /**
     * If single select, the text to display for the "empty" option.
     * Defaults to "Choose an option".
     * (Unused in multi‐select mode because empty selection is controlled differently)
     */
    nullableText?: string;

    /**
     * Allows multiple selections if set to `true`.
     */
    multiple?: boolean;

    /** Custom className for styling */
    className?: string;
}

function GenericSelect<TOption>(
    props: PropsWithChildren<GenericSelectProps<TOption>>
) {
    const {
        options,
        textFunc,
        valueFunc,
        value,
        onChange,
        nullableText = 'Choose an option',
        multiple = false,
        className,
    } = props

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (multiple) {
            // Collect all selected values. Always an array (empty when nothing is
            // selected) so callers can map over the result without a null check.
            const selectedValues = Array.from(e.target.selectedOptions).map(
                (opt) => opt.value
            )
            onChange(selectedValues)
        } else {
            // For single select, if user chooses the "placeholder" option, treat as null
            const selectedValue = e.target.value === '' ? null : e.target.value
            onChange(selectedValue)
        }
    }

    // The "value" prop of <select> should be either a string/number or string[]/number[] in multiple mode
    // If it's null, we pass '' for single‐select or [] for multi‐select
    const selectValue = multiple
        ? (Array.isArray(value) ? value.map(String) : []) // multi‐select (DOM values are strings)
        : (Array.isArray(value) ? '' : value ?? '') // single‐select

    return (
        <select
            multiple={multiple}
            className={classNames('input', className)}
            onChange={handleChange}
            value={selectValue}
        >
            {!multiple && (
                <option value="">
                    {nullableText}
                </option>
            )}

            {options.map((option) => {
                const optValue = valueFunc(option)
                const optText = textFunc(option)
                return (
                    <option key={String(optValue)} value={optValue}>
                        {optText}
                    </option>
                )
            })}
        </select>
    )
}

export default GenericSelect